import type { ValueFormatterFunc } from "ag-grid-enterprise";
import type { ColDef, ColGroupDef } from "~/components/grid/AgGrid";

import logger from "~/helpers/logger";

const safeFormatters = new WeakMap<ValueFormatterFunc, ValueFormatterFunc>();

const safeFormatter = (formatter: ValueFormatterFunc): ValueFormatterFunc => {
	const cached = safeFormatters.get(formatter);
	if (cached) return cached;
	let logged = false;
	const guarded: ValueFormatterFunc = (params) => {
		try {
			return formatter(params);
		} catch (error) {
			if (Array.isArray(params.value)) throw error;
			try {
				return formatter({ ...params, value: params.value == null ? [] : [params.value] });
			} catch {
				if (!logged) {
					logger.error("Grid filter value formatter failed", error, { colId: params.column.getColId() });
					logged = true;
				}
				return params.value == null ? "" : String(params.value);
			}
		}
	};
	safeFormatters.set(formatter, guarded);
	safeFormatters.set(guarded, guarded);
	return guarded;
};

interface FilterParams {
	filters?: { filterParams?: FilterParams; [key: string]: unknown }[];
	valueFormatter?: ValueFormatterFunc;
	[key: string]: unknown;
}

const guardFilterParams = (params: FilterParams): FilterParams => ({
	...params,
	...(typeof params.valueFormatter === "function" ? { valueFormatter: safeFormatter(params.valueFormatter) } : {}),
	...(params.filters
		? {
				filters: params.filters.map((filter) => ({
					...filter,
					...(filter.filterParams ? { filterParams: guardFilterParams(filter.filterParams) } : {})
				}))
			}
		: {})
});

const guardSetFilterFormatters = <TData>(
	columns: (ColDef<TData> | ColGroupDef<TData>)[]
): (ColDef<TData> | ColGroupDef<TData>)[] =>
	columns.map((column) => {
		if ("children" in column) return { ...column, children: guardSetFilterFormatters(column.children) };
		return {
			...column,
			...(typeof column.valueFormatter === "function" ? { valueFormatter: safeFormatter(column.valueFormatter) } : {}),
			...(column.filterParams ? { filterParams: guardFilterParams(column.filterParams) } : {})
		};
	});

export default guardSetFilterFormatters;
